import { LoadingOutlined } from '@ant-design/icons';
import { Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ReactComponent as ApprovalIcon } from 'app/assets/img/approval.svg';
import StyledTable from 'app/components/StyledTable';
import { selectTranslations } from 'app/pages/App/slice/selectors';
import TooltipParagraph from 'app/pages/Catalog/tooltipParagraph';
import moment from 'moment';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { formatNumber } from 'utils/helper';
import {
  PAYMENT_METHOD_LABELS,
  calculateCountdown,
  getTotalQuantity,
} from '../helpers';
import { HolidayOrder, OrderStatus } from '../types';
import { StyledStack } from 'app/components/Stack';
interface OrderTableProps {
  data: HolidayOrder[];
  loading?: boolean;
  onApprove: (order: HolidayOrder) => void;
  onViewDetail: (orderCode: string) => void;
  onExpired?: (orderId: string) => void;
  pagination: {
    current: number;
    pageSize: number;
  };
  tableRef?: React.RefObject<any>;
}

export const OrderTable: React.FC<OrderTableProps> = ({
  data,
  loading,
  onApprove,
  onViewDetail,
  onExpired,
  pagination,
  tableRef,
}) => {
  const translations = useSelector(selectTranslations);
  // State to force re-render for countdown updates
  const [tick, setTick] = useState(0);
  const expiredNotifiedRef = useRef<Set<string>>(new Set());

  // Update countdown every second
  useEffect(() => {
    const interval = setInterval(() => {
      setTick(prev => prev + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Detect expired orders outside render and notify parent once
  useEffect(() => {
    if (typeof onExpired !== 'function') return;
    if (!Array.isArray(data) || data.length === 0) return;

    data.forEach(order => {
      try {
        if (
          order.subOrderState === OrderStatus.PENDING_CONFIRMATION &&
          !expiredNotifiedRef.current.has(order.id)
        ) {
          // Parse with the correct format "HH:mm:ss DD/MM/YYYY"
          let expiredMoment = moment(
            order.expiredAt,
            'HH:mm:ss DD/MM/YYYY',
            true,
          );

          // Fallback to default parsing
          if (!expiredMoment.isValid()) {
            expiredMoment = moment(order.expiredAt);
          }

          const remainingMs = expiredMoment.diff(moment());
          if (remainingMs <= 0) {
            expiredNotifiedRef.current.add(order.id);
            onExpired(order.id);
          }
        }
      } catch (e) {
        // ignore
      }
    });
  }, [data, onExpired, tick]);
  const statusStyles = (status?: OrderStatus) => {
    switch (status) {
      case OrderStatus.CONFIRMED:
        return {
          background: '#E5FCEF',
          color: '#539A59',
        };
      case OrderStatus.PENDING_CONFIRMATION:
        return {
          background: '#FFF5D9',
          color: '#996B0F',
        };
      case OrderStatus.CANCELLED:
      case OrderStatus.UNCONFIRMED:
      default:
        return {
          background: '#E8E8E8',
          color: '#7D7D7D',
        };
    }
  };
  const statusLabel = useCallback(
    (status?: OrderStatus) => {
      switch (status) {
        case OrderStatus.CONFIRMED:
          return translations?.['b2b.approvalStt.success'];
        case OrderStatus.PENDING_CONFIRMATION:
          return translations?.['b2b.approvalStt.pending'];
        case OrderStatus.CANCELLED:
          return translations?.['b2b.txt.ticket-cancelled'];
        case OrderStatus.UNCONFIRMED:
          return translations?.['b2b.approvalStt.refuse'];
        default:
          return '';
      }
    },
    [translations],
  );
  const columns: ColumnsType<HolidayOrder> = useMemo(
    () => [
      {
        title: translations?.['b2b.txt.stt'] ?? '',
        key: 'stt',
        dataIndex: 'stt',
        render: (_: any, __: any, index: number) => {
          const current = pagination.current || 1;
          const pageSize = pagination.pageSize || 20;
          const numberSTT = index + 1 + (current - 1) * pageSize;
          return <div style={{ minWidth: 70 }}>{numberSTT}</div>;
        },
      },
      {
        title: translations?.['b2b.txt.tableAction'] ?? '',
        key: 'action',
        dataIndex: 'action',
        render: (_, record) => {
          const isPending =
            record.subOrderState === OrderStatus.PENDING_CONFIRMATION;
          return (
            <div style={{ minWidth: 100 }}>
              <Tooltip
                title={
                  isPending
                    ? translations?.['b2b.txt.quickApprove'] ||
                      'Phê duyệt nhanh'
                    : translations?.['b2b.txt.notAvailable'] || 'Không khả dụng'
                }
              >
                <StyledApprovalIcon
                  $disable={!isPending}
                  onClick={() => isPending && onApprove(record)}
                />
              </Tooltip>
            </div>
          );
        },
      },
      {
        title: (
          <div style={{ textAlign: 'left' }}>
            {translations?.['b2b.txt.orderDate'] || ''}
          </div>
        ),
        dataIndex: 'createdAt',
        key: 'createdAt',
        render: value => {
          const newDate = moment(value, 'HH:mm:ss DD/MM/YYYY');
          if (newDate.toString() === 'Invalid date')
            return <div style={{ minWidth: 100 }} />;
          return (
            <StyledStack justifyContent={'start'} style={{ minWidth: 100 }}>
              <div style={{ textAlign: 'left' }}>
                <Typography>{newDate.format('DD/MM/YYYY')}</Typography>
                <Typography>{newDate.format('HH:mm:ss')}</Typography>
              </div>
            </StyledStack>
          );
        },
      },
      {
        title: (
          <div style={{ textAlign: 'left' }}>
            {translations?.['b2b.txt.orderCode'] || ''}
          </div>
        ),
        dataIndex: 'submitNumber',
        key: 'submitNumber',
        render: (value, record) => {
          const disable = !record.thirdPartySubmitNumber;
          return (
            <div
              style={{
                color: disable ? '#000' : '#F8B117',
                cursor: disable ? 'default' : 'pointer',
                textAlign: 'left',
                minWidth: 100,
              }}
              onClick={() =>
                disable ? {} : onViewDetail(record.thirdPartySubmitNumber)
              }
            >
              {value}
            </div>
          );
        },
      },
      {
        title: (
          <div style={{ textAlign: 'left' }}>
            {translations?.['b2b.orderManagement.date-order-sap'] || ''}
          </div>
        ),
        dataIndex: 'thirdPartySubmitNumber',
        key: 'thirdPartySubmitNumber',
        render: (code: string) => (
          <div style={{ textAlign: 'left', minWidth: 100 }}>{code}</div>
        ),
      },
      {
        title: (
          <div style={{ textAlign: 'left' }}>
            {translations?.['b2b.txt.paymentMethod'] || ''}
          </div>
        ),
        dataIndex: 'paymentMethod',
        key: 'paymentMethod',
        render: (method: string) => (
          <div style={{ textAlign: 'left', minWidth: 100 }}>
            {PAYMENT_METHOD_LABELS[method] || method}
          </div>
        ),
      },
      {
        title: (
          <div style={{ textAlign: 'center' }}>
            {translations?.['b2b.txt.ticketQuantity'] || ''}
          </div>
        ),
        key: 'quantity',
        dataIndex: 'quantity',
        align: 'center',
        render: (_, record) => (
          <div style={{ minWidth: 80 }}>{getTotalQuantity(record)}</div>
        ),
      },
      {
        title: (
          <div style={{ textAlign: 'right' }}>
            {translations?.['b2b.txt.totalAmount'] || ''}
          </div>
        ),
        dataIndex: 'totalPayment',
        key: 'totalPayment',
        align: 'right',
        render: (amount: number) => (
          <div style={{ minWidth: 80 }}>{`${formatNumber(amount, '.')}đ`}</div>
        ),
      },
      {
        title: (
          <div style={{ textAlign: 'center' }}>
            {translations?.['b2b.txt.holdTime'] || ''}
          </div>
        ),
        dataIndex: 'expiredAt',
        key: 'expiredAt',
        align: 'center',
        render: (expiredAt: string, record: HolidayOrder) => {
          // only display countdown for pending confirmation
          if (record.subOrderState !== OrderStatus.PENDING_CONFIRMATION) {
            return <div style={{ minWidth: 150 }} />;
          }

          const countdown = calculateCountdown(expiredAt);
          const isExpired = countdown === 'Hết hạn';

          // Notify parent once when an order expires
          try {
            let expiredMoment = moment(expiredAt, 'HH:mm:ss DD/MM/YYYY', true);
            if (!expiredMoment.isValid()) {
              expiredMoment = moment(expiredAt);
            }
            const remainingMs = expiredMoment.diff(moment());
            if (
              remainingMs <= 0 &&
              typeof onExpired === 'function' &&
              !expiredNotifiedRef.current.has(record.id)
            ) {
              expiredNotifiedRef.current.add(record.id);
              onExpired!(record.id);
            }
          } catch {
            // ignore parse errors
          }
          return (
            <div style={{ minWidth: 150 }}>
              <CountdownText expired={isExpired}>{countdown}</CountdownText>
            </div>
          );
        },
      },
      {
        title: (
          <div style={{ textAlign: 'left' }}>
            {translations?.['b2b.txt.orderedByAgent'] || ''}
          </div>
        ),
        dataIndex: 'agent',
        key: 'agent',
        render: (agent: string) => (
          <div style={{ textAlign: 'left', minWidth: 120 }}>{agent}</div>
        ),
      },
      {
        title: (
          <div style={{ textAlign: 'center' }}>
            {translations?.['b2b.txt.status'] || ''}
          </div>
        ),
        dataIndex: 'subOrderState',
        key: 'subOrderState',
        render: (status: OrderStatus) => {
          const statusStyle = statusStyles(status);
          return (
            <div style={{ textAlign: 'center', minWidth: 120 }}>
              <StyledStatus style={statusStyle}>
                {statusLabel(status)}
              </StyledStatus>
            </div>
          );
        },
      },
      {
        title: (
          <table className="approval-order-title-table">
            <tbody>
              <tr>
                <TitleCell>
                  {translations?.['b2b.txt.implementer'] ?? ''}
                </TitleCell>
                <TitleCell>
                  {translations?.['b2b.txt.approvalHistory'] ?? ''}
                </TitleCell>
              </tr>
            </tbody>
          </table>
        ),
        dataIndex: 'logConfirmation',
        key: 'logConfirmation',
        render: value => {
          return (
            <table className="approval-order-body-table">
              <tbody>
                {!value.length && (
                  <tr>
                    <BodyCell></BodyCell>
                    <BodyCell>
                      <span></span>
                      <span></span>
                    </BodyCell>
                  </tr>
                )}
                {value?.length > 0 &&
                  value.map((item, idx) => {
                    const [date, time] = item?.confirmedAt
                      ? item?.confirmedAt?.split(' ')
                      : '';
                    return (
                      <tr
                        key={`${item?.confirmer ?? 'na'}-${
                          item?.confirmedAt ?? 'na'
                        }-${idx}`}
                      >
                        <BodyCell>
                          <TooltipParagraph
                            ellipsis={{ rows: 1 }}
                            style={{
                              marginBottom: 0,
                              maxWidth: 164,
                            }}
                          >
                            {item?.confirmer}
                          </TooltipParagraph>
                        </BodyCell>
                        <BodyCell
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            height: '50px',
                          }}
                        >
                          <span>{date ?? ''}</span>
                          <span>{time ?? ''}</span>
                        </BodyCell>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          );
        },
      },
    ],
    [translations, onApprove, onExpired, onViewDetail, pagination, statusLabel],
  );
  return (
    <StyledTable
      tableRef={tableRef}
      dataSource={data}
      columns={columns as any}
      pagination={false}
      tableProps={{
        // use functional key to guarantee uniqueness even if id is missing
        rowKey: (record: HolidayOrder) => record.id,
        scroll: {
          x: data?.length ? 'auto' : 'max-content',
          y: data?.length ? '50vh' : undefined,
        },
        loading: {
          spinning: loading,
          indicator: <LoadingOutlined style={{ fontSize: 24 }} spin />,
        },
      }}
      styleSlots={{
        antTableContainer: {
          overflow: 'hidden',
        },
        antTableHeader: {
          border: 'none !important',
          borderRadius: '0 !important',
        },
        tableCSS: {
          borderTopLeftRadius: !data?.length ? '' : '0 !important',
          borderTopRightRadius: !data?.length ? '' : '0 !important',
        },
        antTableBody: {
          paddingBottom: 10,
        },
      }}
      emptyText={
        translations?.['b2b.txt.noOrderFound'] || 'Không tìm thấy đơn hàng nào'
      }
    />
  );
};

const StyledApprovalIcon = styled(ApprovalIcon)<{ $disable?: boolean }>`
  cursor: ${props => (props.$disable ? 'auto' : 'pointer')};
  rect {
    fill: transparent;
  }
  path {
    stroke: ${props => (props.$disable ? '#D9D9D9' : '#FF7B01')};
  }
`;

const CountdownText = styled.span<{ expired?: boolean }>`
  font-weight: 600;
  color: ${props => (props.expired ? '#e92127' : '#e92127')};
`;
const StyledStatus = styled.div`
  width: 'auto';
  height: 25px;
  min-width: 100px;
  padding: 1.5px 10px;
  border-radius: 50px;
  font-size: 12px;
  line-height: 22px;
  letter-spacing: 2%;
  line-height: 22px;
  margin: auto;
  white-space: nowrap;
  display: inline-block;
`;
const TitleCell = styled.td`
  padding: 15px 10px;
  min-width: 150px;
`;

const BodyCell = styled.td`
  max-width: 150px;
  min-width: 150px;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`;
